import { Link } from 'react-router-dom';
import { Input } from '../ui/input';
import { Button } from '../ui/button';
import { useState } from 'react';
import { newsletterAPI } from '../../lib/api';
import { toast } from 'sonner';
import { ArrowRight } from 'lucide-react';

export const Footer = () => {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubscribe = async (e) => {
    e.preventDefault();
    if (!email) return;
    
    setLoading(true);
    try {
      await newsletterAPI.subscribe(email);
      toast.success('Successfully subscribed to newsletter!');
      setEmail('');
    } catch (err) {
      toast.error('Failed to subscribe. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const footerSections = [
    {
      title: 'Directory',
      links: [
        { label: 'Communities', href: '/communities' },
        { label: 'Startups', href: '/startups' },
        { label: 'Events', href: '/events' },
        { label: 'Jobs', href: '/jobs' },
      ]
    },
    {
      title: 'Resources',
      links: [
        { label: 'Topics', href: '/topics' },
        { label: 'Blog', href: '/blog' },
        { label: 'Forums', href: '/forums' },
        { label: 'Newsletter', href: '/newsletter' },
      ]
    },
    {
      title: 'About',
      links: [
        { label: 'About Portland Tech', href: '/about' },
        { label: 'Get Involved', href: '/get-involved' },
        { label: 'Contact', href: '/contact' },
        { label: 'Partnerships', href: '/partnerships' },
      ]
    },
  ];

  return (
    <footer className="border-t border-border bg-muted/30" data-testid="footer">
      <div className="container-wide py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-12">
          {/* Brand & Newsletter */}
          <div className="lg:col-span-2">
            <Link to="/" className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 bg-primary rounded-sm flex items-center justify-center">
                <span className="text-primary-foreground font-bold">PDX</span>
              </div>
              <div>
                <span className="font-bold text-lg tracking-tight block">Portland Tech</span>
                <span className="text-sm text-muted-foreground">Ecosystem</span>
              </div>
            </Link>
            
            <p className="text-sm text-muted-foreground mb-6 max-w-sm">
              Connecting Portland's technology community. Discover startups, events, 
              communities, and opportunities in Portland's tech scene.
            </p>

            {/* Newsletter signup */}
            <div className="mb-6">
              <h4 className="text-xs font-mono uppercase tracking-widest text-muted-foreground mb-3">
                Subscribe to Newsletter
              </h4>
              <form onSubmit={handleSubscribe} className="flex gap-2">
                <Input
                  type="email"
                  placeholder="your@email.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="flex-1 h-10"
                  data-testid="footer-newsletter-input"
                />
                <Button 
                  type="submit" 
                  size="sm" 
                  disabled={loading}
                  className="h-10 px-4"
                  data-testid="footer-newsletter-submit"
                >
                  <ArrowRight className="w-4 h-4" />
                </Button>
              </form>
            </div>
          </div>

          {/* Footer Links */}
          {footerSections.map(section => (
            <div key={section.title}>
              <h4 className="text-xs font-mono uppercase tracking-widest text-muted-foreground mb-4">
                {section.title}
              </h4>
              <ul className="space-y-3">
                {section.links.map(link => (
                  <li key={link.href}>
                    <Link 
                      to={link.href}
                      className="text-sm text-muted-foreground hover:text-foreground transition-colors"
                    >
                      {link.label}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        {/* Bottom bar */}
        <div className="border-t border-border mt-12 pt-8 flex flex-col sm:flex-row justify-between items-center gap-4">
          <p className="text-xs text-muted-foreground">
            © {new Date().getFullYear()} Portland Technology Ecosystem. All rights reserved.
          </p>
          <div className="flex items-center gap-6 text-xs text-muted-foreground">
            <Link to="/privacy" className="hover:text-foreground transition-colors">
              Privacy Policy
            </Link>
            <Link to="/terms" className="hover:text-foreground transition-colors">
              Terms of Service
            </Link>
          </div>
        </div>
      </div>
    </footer>
  );
};
